import {
  sortableQuestionColumns,
  type SortableQuestion,
} from "./-components/sortable-question-columns"
import type { Id } from "@convex/_generated/dataModel"
import { useState, useEffect, useMemo } from "react"
import { useMutation, useQuery } from "convex/react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronLeft } from "lucide-react"
import { type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { z } from "zod"

import { api } from "@convex/_generated/api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { SortableDataTable } from "@/components/ui/sortable-data-table"

const roundQuestionsSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
})

export const Route = createFileRoute(
  "/(admin)/admin/quizzes/$quizId_/$roundNumber"
)({
  component: RouteComponent,
  validateSearch: roundQuestionsSearchSchema,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { quizId, roundNumber } = Route.useParams()
  const { search } = Route.useSearch()
  const roundNum = parseInt(roundNumber, 10)

  const quiz = useQuery(api.quizzes.handlers.getById, {
    quizId: quizId as Id<"quizzes">,
  })

  const allQuestions = useQuery(
    api.quizzesToQuestions.handlers.getQuestionsByQuizId,
    {
      quizId: quizId as Id<"quizzes">,
    }
  )

  const reorderQuestions = useMutation(
    api.quizzesToQuestions.handlers.reorderQuestions
  )

  const roundQuestions = useMemo(() => {
    if (!allQuestions) return []
    return allQuestions.filter((q) => q.roundNumber === roundNum)
  }, [allQuestions, roundNum])

  const [items, setItems] = useState<SortableQuestion[]>([])

  // Update local state when data loads
  useEffect(() => {
    if (roundQuestions.length > 0) {
      setItems(roundQuestions as SortableQuestion[])
    }
  }, [roundQuestions])

  const handleSearchFilterChange = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        search: value || undefined,
      }),
    }).catch((error: unknown) => {
      console.error("Navigation error:", error)
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setItems((currentItems) => {
        const oldIndex = currentItems.findIndex(
          (item) => item._id === (active.id as Id<"questions">)
        )
        const newIndex = currentItems.findIndex(
          (item) => item._id === (over.id as Id<"questions">)
        )

        if (oldIndex === -1 || newIndex === -1) return currentItems

        const newItems = arrayMove(currentItems, oldIndex, newIndex)

        // Update the order in the backend
        const reorderedQuestions = newItems.map(
          (item: SortableQuestion, index: number) => ({
            questionId: item._id,
            newOrder: index,
          })
        )

        void reorderQuestions({
          quizId: quizId as Id<"quizzes">,
          roundNumber: roundNum,
          reorderedQuestions,
        })

        return newItems
      })
    }
  }

  if (quiz === undefined || allQuestions === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (quiz === null) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="text-3xl font-bold">Quiz not found</h1>
        <p className="text-muted-foreground">
          The quiz you're looking for doesn't exist.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/quizzes/$quizId" params={{ quizId }}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {quiz.name} - Round {roundNum}
          </h1>
          <p className="text-sm text-muted-foreground">
            Drag and drop to reorder questions
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Round {roundNum} Questions ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No questions assigned to this round yet. Assign a deck from the
              quiz detail page.
            </div>
          ) : (
            <SortableDataTable
              columns={sortableQuestionColumns}
              data={items}
              filterColumn="text"
              filterPlaceholder="Filter questions..."
              filterValue={search}
              onFilterChange={handleSearchFilterChange}
              onDragEnd={handleDragEnd}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
